import { useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useLantern } from "../hooks";
import { canInstall, install, installed } from "../install";
import { navigate } from "../router";
import { t } from "../i18n";
import { IconDesktop, IconList, IconPeople, IconPersonAdd, IconShield, IconSmile, IconWorkflow } from "./icons";
import { useMenu, usePlace } from "./ui";

type Pages = [string, string, ReactNode][];

// The workspace's pages: everyone's, then admins': Administration, whose
// own sidebar holds the rest, and the agents' work, watched beside the
// channels. One's own pages are in the account menu instead.
const pages = (): Pages => [
  [t("People"), "/people", <IconPeople size={16} />],
  [t("Custom emoji"), "/emoji", <IconSmile size={16} />],
];
const adminPages = (): Pages => [
  [t("Administration"), "/admin/workspace", <IconShield size={16} />],
  [t("Fleet"), "/fleet", <IconList size={16} />],
  [t("Workflows"), "/workflows", <IconWorkflow size={16} />],
];

// initials is a workspace's name as a tile's letters.
export function initials(name: string) {
  const words = name.split(/[\s_-]+/).filter((w) => /^[\p{L}\p{N}]/u.test(w));
  return (words.length > 1 ? words[0][0] + words[1][0] : (words[0] ?? "L").slice(0, 2)).toUpperCase();
}

// WorkspaceMenu opens from the workspace's name atop the sidebar: which
// workspace and server this is, inviting people, the workspace's pages and
// admins' pages, and installing the desktop app.
export function WorkspaceMenu({ anchor, onClose }: { anchor: DOMRect; onClose: () => void }) {
  const state = useLantern();
  const ref = useRef<HTMLDivElement>(null);
  const name = state.workspace || "Lantern";
  const admin = state.me?.role === "admin";

  useMenu(ref, onClose);
  const style = usePlace(ref, anchor);

  const go = (path: string) => () => {
    onClose();
    navigate(path);
  };
  const offer = !installed() && canInstall();
  return createPortal(
    <div ref={ref} className="workspace-menu" role="menu" aria-label={t("{name} menu", { name })} style={style}>
      <div className="workspace-card">
        <span className="workspace-tile" aria-hidden>
          {initials(name)}
        </span>
        <span>
          <strong>{name}</strong>
          <span className="muted">{location.host}</span>
        </span>
      </div>
      {admin && (
        <div className="workspace-group">
          <button role="menuitem" onClick={go("/admin/invites")}>
            <IconPersonAdd size={16} />
            {t("Invite people to {name}", { name })}
          </button>
        </div>
      )}
      <div className="workspace-group">
        {pages().map(([label, path, icon]) => (
          <button key={path} role="menuitem" onClick={go(path)}>
            {icon}
            {label}
          </button>
        ))}
      </div>
      {admin && (
        <div className="workspace-group" role="group" aria-label={t("Admins")}>
          {adminPages().map(([label, path, icon]) => (
            <button key={path} role="menuitem" onClick={go(path)}>
              {icon}
              {label}
            </button>
          ))}
        </div>
      )}
      {(offer || installed()) && (
        <div className="workspace-group">
          {offer ? (
            <button role="menuitem" onClick={() => (onClose(), void install())}>
              <IconDesktop size={16} />
              {t("Install the desktop app")}
            </button>
          ) : (
            <p className="quiet">{t("You are using the desktop app.")}</p>
          )}
        </div>
      )}
    </div>,
    document.body,
  );
}
